import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { parseSmart, buildSearchParams, filtersToText, POS_CODES, US_STATES, EMPTY_FILTERS } from '../../lib/athleteSearch';
import { initials, posAbbrev, fmtHt, classOf } from './listFormat';
import FilterChip from './FilterChip';

const PAGE = 20;
const DEBOUNCE_MS = 300;

const fmtCount = (n) => Number(n || 0).toLocaleString('en-US');
const rowMeta = (p) => [
  posAbbrev(p.position), classOf(p), p.state, fmtHt(p.height_inches), p.weight_lbs ? String(Math.round(p.weight_lbs)) : null,
].filter((x) => x && x !== '—').join(' · ');

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
);

// The "Find & add athletes" tab of an open list. The page owns { text, page } so the query
// survives tab switches; filters are always parsed from the text, and every chip writes back
// through filtersToText, so the chips and the typed text never disagree. With nothing typed the
// panel browses the sport's newest profiles. Athletes already on the list stay in the results,
// marked "On this list", so an empty panel only ever means nobody matches.
export default function ListFindTab({ sport, campList = [], members = [], positions = [], onAdd, state, onState }) {
  const { API_URL } = useAuth();
  const text = state?.text || '';
  const page = state?.page || 1;
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(true);
  const [failed, setFailed] = useState('');

  const filters = useMemo(() => parseSmart(text, campList, positions), [text, campList, positions]);
  // A stable key so a new campList/positions array that parses the same does not refetch.
  const key = JSON.stringify(filters);
  const isFootball = sport === 'football';

  // What `results` currently holds: { key, page }. "Show 20 more" fetches only the next page (by offset — the
  // server caps `limit` at 100, so growing the limit stalls at page 5) and appends it; anything else (a new
  // query, a remount on page N) loads pages 1..N afresh.
  const loaded = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const prev = loaded.current;
      const loadKey = `${sport}|${key}`;
      const append = prev && prev.key === loadKey && prev.page === page - 1;
      const pages = append ? [page] : Array.from({ length: page }, (_, i) => i + 1);
      try {
        const answers = await Promise.all(pages.map((n) => {
          const params = buildSearchParams(JSON.parse(key), sport, isFootball, PAGE);
          params.append('offset', String(PAGE * (n - 1)));
          params.append('sort', 'newest');
          return axios.get(`${API_URL}/athletes/search?${params.toString()}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        }));
        if (cancelled) return;
        const players = answers.flatMap((res) => res.data.players || []);
        const last = answers[answers.length - 1].data;
        // De-duplicated by user_id: a profile created between two page requests shifts the offsets by one.
        setResults((cur) => {
          const seen = new Set();
          return [...(append ? cur : []), ...players].filter((p) => !seen.has(p.user_id) && seen.add(p.user_id));
        });
        setTotal(typeof last.total === 'number' ? last.total : players.length);
        setFailed('');
        loaded.current = { key: loadKey, page };
      } catch (e) {
        if (cancelled) return;
        const data = e.response?.data || {};
        setResults([]); setTotal(0);
        loaded.current = null;
        setFailed((typeof data.detail === 'string' && data.detail) || (typeof data.error === 'string' && data.error)
          || (e.response?.status === 429 ? 'Too many searches. Wait a minute and try again.' : 'Search failed.'));
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [key, page, sport, isFootball, API_URL]);

  const onList = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);

  const setText = (next) => onState({ text: next, page: 1 });
  const setFilter = (k, v) => setText(filtersToText({ ...filters, [k]: v }, campList));

  const year = new Date().getFullYear();
  // Multi-sport: football uses its short codes; every other sport only the catalog's names.
  const posOptions = (isFootball ? POS_CODES : positions).map((p) => ({ value: p, label: p }));
  const classOptions = [0, 1, 2, 3, 4].map((i) => ({ value: String(year + i), label: `Class of ${year + i}` }));
  const stateOptions = US_STATES.map((s) => ({ value: s, label: s }));
  const campOptions = campList.map((c) => ({ value: String(c.id), label: c.name }));
  const campName = (campList.find((c) => String(c.id) === filters.camp) || {}).name;

  const chipBits = [
    filters.pos, filters.grad && `Class of ${filters.grad}`, filters.state, filters.camp && (campName || 'Camp'),
  ].filter(Boolean);
  const anyFilter = Object.keys(EMPTY_FILTERS).some((k) => filters[k]);
  let headLeft;
  if (searching) headLeft = 'Searching…';
  else if (failed) headLeft = chipBits.join(' · ') || 'Search';
  else if (filters.q) headLeft = [...chipBits, `${fmtCount(total)} match "${filters.q}"`].join(' · ');
  else if (anyFilter) headLeft = [...chipBits, `${fmtCount(total)} ${total === 1 ? 'athlete' : 'athletes'}`].join(' · ');
  else headLeft = `All ${String(sport || '').replace(/[-_]/g, ' ')} athletes · ${fmtCount(total)}`;

  return (
    <div className="ls-findtab" data-testid="list_find">
      <div className="ls-find__top">
        <div className="ls-searchbox">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
          </svg>
          <input className="ls-searchbox__input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Search by name…" aria-label="Search athletes" data-testid="list_add_input" />
        </div>
        <div className="ls-chips">
          {posOptions.length > 0 && (
            <FilterChip name="Position" testId="list_chip_position" value={filters.pos} options={posOptions}
              onPick={(v) => setFilter('pos', v)} />
          )}
          <FilterChip name="Class" testId="list_chip_class" value={filters.grad}
            valueLabel={filters.grad ? `Class of ${filters.grad}` : ''} options={classOptions} onPick={(v) => setFilter('grad', v)} />
          <FilterChip name="State" testId="list_chip_state" value={filters.state} options={stateOptions}
            onPick={(v) => setFilter('state', v)} />
          <FilterChip name="Camp" testId="list_chip_camp" value={filters.camp} valueLabel={campName} options={campOptions}
            onPick={(v) => setFilter('camp', v)} />
          {text && <button type="button" className="ls-chips__clear" onClick={() => setText('')} data-testid="list_add_clear">Clear</button>}
        </div>
      </div>
      <div className="ls-results ls-find__results" data-testid="list_results">
        <div className="ls-results__head" data-testid="list_results_head">
          <span>{headLeft}</span>
          <span>Newest profiles first</span>
        </div>
        {failed && <p className="ls-empty" role="alert">{failed}</p>}
        {!failed && !searching && !results.length && <p className="ls-empty">No athletes match.</p>}
        {!failed && results.map((p) => (
          <div className="ls-rrow" key={p.user_id} data-testid={`list_result_${p.user_id}`}>
            <span className="ls-avatar" aria-hidden="true">{initials(p.name)}</span>
            <span className="ls-rrow__text">
              <Link to={`/athletes/${p.user_id}`} className="ls-rrow__name">{p.name}</Link>
              <span className="ls-rrow__meta">{rowMeta(p)}</span>
            </span>
            {onList.has(p.user_id) ? (
              <span className="ls-onlist" data-testid={`list_onlist_${p.user_id}`}><CheckIcon />On this list</span>
            ) : (
              <button type="button" className="ls-btn ls-btn--primary ls-btn--tiny" onClick={() => onAdd(p)}
                data-testid={`list_add_${p.user_id}`} aria-label={`Add ${p.name}`}>+ Add</button>
            )}
          </div>
        ))}
        {!failed && results.length > 0 && results.length < total && (
          <button type="button" className="ls-results__more" disabled={searching}
            onClick={() => onState({ text, page: page + 1 })} data-testid="list_results_more">{`Show ${PAGE} more`}</button>
        )}
      </div>
    </div>
  );
}
